import { delay } from '@/utils/delay';

// Waits between attempts: two retries, three attempts in total.
const RETRY_DELAYS_MS = [300, 1000];

// Upper bound for a single attempt, so a stalled socket (ETIMEDOUT) cannot hold
// the request until the OS gives up. Villa pages already take ~10s in total.
const ATTEMPT_TIMEOUT_MS = 15_000;

type FetchWithRetryOptions = {
  /**
   * Allow retrying methods other than GET/HEAD. Only for requests that are
   * safe to send twice (e.g. login), since the first one may have reached the
   * server.
   */
  retryUnsafe?: boolean;
};

type ErrorWithCause = {
  cause?: { code?: string; errors?: { code?: string }[] };
};

/**
 * undici throws a bare TypeError('fetch failed') when the connection drops
 * before a response arrives (ECONNRESET, socket closed, ETIMEDOUT). HTTP error
 * statuses resolve normally and never get here.
 */
function isNetworkError(error: unknown): boolean {
  return error instanceof TypeError && error.message === 'fetch failed';
}

/**
 * System error code behind a failed fetch, for logging. A connection attempt to
 * several addresses fails with an AggregateError, whose codes sit in `errors`.
 */
export function networkErrorCode(error: unknown): string | undefined {
  const cause = (error as ErrorWithCause | undefined)?.cause;

  return cause?.code ?? cause?.errors?.[0]?.code;
}

/**
 * `fetch` that retries on transient network failures. The calls to the
 * next-admin and vt-split go through Cloudflare, and that connection
 * occasionally drops before answering. Uses the global fetch, so Next's
 * `cache` / `next.revalidate` options keep working.
 *
 * The same `init` is sent on every attempt, so `body` must be replayable
 * (a string, not a stream).
 */
export async function fetchWithRetry(
  input: string,
  init?: RequestInit,
  { retryUnsafe = false }: FetchWithRetryOptions = {},
): Promise<Response> {
  const method = (init?.method ?? 'GET').toUpperCase();
  const canRetry = retryUnsafe || method === 'GET' || method === 'HEAD';

  for (let attempt = 0; ; attempt++) {
    try {
      return await fetch(input, {
        ...init,
        // A caller's own signal wins over the per-attempt timeout.
        signal: init?.signal ?? AbortSignal.timeout(ATTEMPT_TIMEOUT_MS),
      });
    } catch (error) {
      if (
        !canRetry ||
        !isNetworkError(error) ||
        attempt >= RETRY_DELAYS_MS.length
      ) {
        throw error;
      }

      console.warn('[FETCH-RETRY]', {
        // Query strings can carry search terms; the path is enough to trace.
        url: input.split('?')[0],
        method,
        attempt: attempt + 1,
        code: networkErrorCode(error),
      });

      await delay(RETRY_DELAYS_MS[attempt]);
    }
  }
}
